import type FetchError from './errors.tsx';

export const isHttpClientErrorResponse = (error: FetchError | Error): boolean =>
	typeof error === 'object' &&
	'statusCode' in error &&
	error.statusCode >= 400 &&
	error.statusCode < 500;

export const isHttpClientErrorStatusCode = (statusCode: number | null): boolean =>
	statusCode !== null && statusCode >= 400 && statusCode < 500;

export const isHttpTooManyRequestsResponse = (error: FetchError): boolean =>
	error.statusCode === 429;

// Exclude client connectivity and HTTP client network errors as a SLO failure
// e.g. during quick SPA transition when a network call haven't finished yet,
// or when the user's internet connection drops out.
export const KNOWN_NETWORK_ERRORS = [
	'... is not valid JSON',
	'server with the specified hostname could not be found',
	'cannot parse response',
	'connection failure',
	'Failed to fetch',
	'in JSON at position 0',
	'JSON.parse: unexpected character at line 1 column 1 of the JSON data',
	'JSON.parse: unexpected end of data at line 1 column 1 of the JSON data',
	'Load failed',
	'Network request failed',
	'NetworkError',
	'Syntax or http error',
	'The request timed out',
	'The network connection was lost',
	'The operation was aborted',
	'Unable to fetch manifest',
	'Unexpected end of input',
	'Unexpected end of JSON input',
	'dynamically imported module',
	'Importing a module script failed',
];
export const KNOWN_NETWORK_ERRORS_TYPES = [
	'AbortError',
	'AuthError',
	'ChunkLoadError',
	'NetworkError',
	'Network error',
];
const isClientFetchErrorMessage = (message: string): boolean =>
	Boolean(message) &&
	!!KNOWN_NETWORK_ERRORS.find((networkErrorMessage) =>
		// Using `includes` here because the underlying fetch error messages
		// are sometimes wrapped to provide additional context, e.g.
		// "Something went wrong: ${error.message}"
		message.includes(networkErrorMessage),
	);
/**
 * Checks if an error stems from client-side network issues, considering both direct and nested error information.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isClientFetchError = (error: any) =>
	Boolean(error) &&
	typeof error === 'object' &&
	(KNOWN_NETWORK_ERRORS_TYPES.includes(error.name) ||
		isClientFetchErrorMessage(error.message) ||
		(typeof error.cause === 'object' &&
			(KNOWN_NETWORK_ERRORS_TYPES.includes(error.cause.name) ||
				isClientFetchErrorMessage(error.cause.message))));
export const KNOWN_BACKEND_ERRORS = [
	'authProviderTimedOut',
	'Call to graphql returned empty issue',
	'endpoint failed with status',
	'Exception while fetching data',
	'fetch-error: no data or no data node',
	'FetchError',
	'GraphQL error',
	'GraphQLNoResultsError',
	'HTTP status',
	'java.lang.',
	'java.net.',
	'java.util.',
	'javax.net.',
	'JSON at position',
	'Network error:',
	'not valid JSON',
	'Problem while calling endpoint',
	'RelayNetwork',
	'Response ended with an error',
	'serverForbidden',
	'serverInvalidBody',
	'serverUnauthorized',
	'serverUnexpectedError',
];

/**
 * Determines if an error is due to backend API calls by examining status codes, GraphQL errors, and known error patterns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isBackendAPIError = (error: any) =>
	Boolean(error) &&
	typeof error === 'object' &&
	('statusCode' in error ||
		(Boolean(error.cause) && typeof error.cause === 'object' && 'statusCode' in error.cause) ||
		'graphQLErrors' in error ||
		(Boolean(error.cause) && typeof error.cause === 'object' && 'graphQLErrors' in error.cause) ||
		KNOWN_BACKEND_ERRORS.some((v) =>
			`${error.name}${error.message}${error.cause?.name}${error.cause?.message}`.includes(v),
		));

/**
 * Remove isBackendAPIErrorOld when cleaning up refactor_isbackendapierror_check_logic
 * Determines if an error is due to backend API calls by examining status codes, GraphQL errors, and known error patterns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isBackendAPIErrorOld = (error: any) =>
	Boolean(error) &&
	typeof error === 'object' &&
	('statusCode' in error ||
		(error.cause && 'statusCode' in error.cause) ||
		'graphQLErrors' in error ||
		(error.cause && 'graphQLErrors' in error.cause) ||
		KNOWN_BACKEND_ERRORS.some((v) =>
			(error.name + error.message + error.cause?.name + error.cause?.message).includes(v),
		));
